/**
 * Devtool-only NATS-outage simulator.
 *
 * Wraps the heartbeat's `client.status()` async-iterable with a control
 * channel so an engineer can inject a synthetic `disconnect` → (N ms) →
 * `reconnect` WITHOUT touching the real WebSocket. This drives the real
 * heartbeat → orchestrator → hysteresis → FSM → `connectivityStore` →
 * degraded-poll path end-to-end (so polling behaviour is observable),
 * while the live socket — and therefore `connectSingleton` — is never
 * torn down, guaranteeing recovery when the window ends.
 *
 * Production: `wrapStatusForOutageSim` returns the real iterable
 * UNCHANGED when `!isDevtoolEnabled()` (zero overhead, DCE-trivial), and
 * `simulateNatsOutage` is a no-op. Engineering-only surface — exported
 * from `devtool/index.ts`, never the main barrel.
 *
 * Single-consumer invariant: only `SDKProvider`'s `installNatsHeartbeat`
 * call passes a `client.status()` iterable through here, so the module's
 * control state is a process singleton with exactly one reader.
 */

import type { NatsStatusEvent } from '../connectivity/lifecycle/nats-heartbeat.js';
import { isDevtoolEnabled } from './events.js';

/** Default outage length (ms). The sandbox buttons pass this explicitly. */
const DEFAULT_OUTAGE_MS = 20_000;

/**
 * globalThis pin so the HTTP debug bridges (`sdk-debug-web` /
 * `sdk-debug-rn`) can trigger an outage headlessly. Matches the
 * `__morfi_*` single-source convention in `nats/connect/globals.ts`.
 */
const SIMULATE_GLOBAL_KEY = '__morfi_simulateNatsOutage' as const;

type TimerId = ReturnType<typeof setTimeout>;

interface Injectables {
  readonly setTimeout: (cb: () => void, ms: number) => TimerId;
  readonly clearTimeout: (id: TimerId) => void;
}

const defaultInjectables: Injectables = {
  setTimeout: (cb, ms) => setTimeout(cb, ms),
  clearTimeout: (id) => clearTimeout(id),
};

let injectables: Injectables = defaultInjectables;

// ── Control state (module singleton; exactly one consumer) ─────────────
let outageActive = false;
let outageTimer: TimerId | null = null;
const controlQueue: NatsStatusEvent[] = [];

/**
 * Single-slot notify signal. `wait()` returns the current pending
 * promise (the SAME instance for repeated calls until `notify()`), so
 * the generator never accumulates stale resolvers. `notify()` swaps in
 * a fresh promise BEFORE resolving the old one, so a wake-up is never
 * lost between iterations.
 */
function makeSignal(): { wait: () => Promise<void>; notify: () => void } {
  let resolve: () => void = () => {};
  let p = new Promise<void>((r) => {
    resolve = r;
  });
  return {
    wait: () => p,
    notify: () => {
      const fire = resolve;
      p = new Promise<void>((r) => {
        resolve = r;
      });
      fire();
    },
  };
}

let signal = makeSignal();

/**
 * Start (or restart) a simulated NATS outage of `ms` milliseconds. The
 * wrapped status stream emits a synthetic `disconnect` immediately and a
 * synthetic `reconnect` after `ms`; real status events are suppressed
 * (but still drained) for the window so a real pong can't cancel the
 * synthetic DEGRADED state. No-op unless devtool is enabled.
 */
export function simulateNatsOutage(ms: number = DEFAULT_OUTAGE_MS): void {
  if (!isDevtoolEnabled()) return;
  if (outageTimer !== null) {
    injectables.clearTimeout(outageTimer);
    outageTimer = null;
  }
  outageActive = true;
  controlQueue.push({ type: 'disconnect' });
  signal.notify();
  outageTimer = injectables.setTimeout(() => {
    outageActive = false;
    outageTimer = null;
    controlQueue.push({ type: 'reconnect' });
    signal.notify();
  }, ms);
}

/**
 * Wrap a NATS status async-iterable so simulated outages are injected.
 * Returns `real` UNCHANGED when devtool is disabled (zero overhead).
 */
export function wrapStatusForOutageSim(
  real: AsyncIterable<NatsStatusEvent>,
): AsyncIterable<NatsStatusEvent> {
  if (!isDevtoolEnabled()) return real;
  // Pin the headless trigger now the devtool flag is definitively live
  // (this runs from SDKProvider's orchestrator effect, after setup).
  (globalThis as { [SIMULATE_GLOBAL_KEY]?: typeof simulateNatsOutage })[SIMULATE_GLOBAL_KEY] =
    simulateNatsOutage;

  return {
    async *[Symbol.asyncIterator](): AsyncIterator<NatsStatusEvent> {
      const it = real[Symbol.asyncIterator]();
      // Reused across iterations so we never call `it.next()` twice
      // concurrently (illegal for the nats-core status iterator).
      let realNext: Promise<IteratorResult<NatsStatusEvent>> | null = null;
      try {
        while (true) {
          // Synthetic events (disconnect/reconnect) take priority and are
          // drained at the top of every iteration.
          while (controlQueue.length > 0) {
            const ev = controlQueue.shift();
            if (ev !== undefined) yield ev;
          }
          if (realNext === null) realNext = it.next();
          const outcome = await Promise.race([
            realNext.then((r) => ({ kind: 'real' as const, r })),
            signal.wait().then(() => ({ kind: 'ctrl' as const })),
          ]);
          if (outcome.kind === 'real') {
            realNext = null;
            if (outcome.r.done === true) return;
            // Pass real events through ONLY when no outage is active;
            // during an outage we drop them but keep draining the real
            // iterator so it stays live for post-window recovery.
            if (!outageActive) yield outcome.r.value;
          }
          // kind === 'ctrl': loop. `realNext` stays pending (reused);
          // the queued synthetic event is drained at the next loop top.
        }
      } finally {
        await it.return?.();
      }
    },
  };
}

/** Test-only — reset all module state and restore real timers. */
export function _resetForTests(): void {
  if (outageTimer !== null) {
    injectables.clearTimeout(outageTimer);
  }
  outageActive = false;
  outageTimer = null;
  controlQueue.length = 0;
  signal = makeSignal();
  injectables = defaultInjectables;
}

/** Test-only — inject deterministic timers. */
export function _setInjectablesForTests(next: Partial<Injectables>): void {
  injectables = { ...injectables, ...next };
}
